/** Simple email regex for basic format validation (e.g. comma-separated lists). */
export const SIMPLE_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// How many rows to show per page by default
export const TABLE_ROW_COUNT_DEFAULT = 25;
// Row count options for tables
export const TABLE_ROW_COUNT_OPTIONS = [25, 50, 100];

// Audit log descriptions
export const AUDIT_LOG_DESCRIPTIONS = Object.freeze({
    login: 'User has logged in.',
    create_arrangement: 'A seat arrangement has been created.',
    delete_arrangement: 'A seat arrangement has been deleted.',
    reset_default_password: 'User password reset request during create user.',
    reset_password_request: 'User password reset request.',
    reset_password: 'User password reseted.',
    force_set_password: 'Password was force-reset by an administrator.',
    two_factor_reset: 'Two-factor authentication has been reset.',
    two_factor_attempt: 'A two-factor authentication attempt has been made.',
    two_factor_verified: 'Two-factor authentication has been verified.',
    two_factor_qr_code_generated: 'A two-factor QR code has been generated.',
    switch_venue: 'User has switched venue.',
    add_venue: 'A venue has been added.',
    delete_venue: 'A venue has been deleted.',
    edit_venue: 'A venue has been edited.',
    import_venue: 'A venue has been imported.',
    add_user: 'A user has been added.',
    delete_user: 'A user has been deleted.',
    edit_user: 'A user has been edited.',
    add_show: 'A show has been added.',
    delete_show: 'A show has been deleted.',
    edit_show: 'A show has been edited.',
    import_show: 'A show has been imported.',
    import_show_merged: 'An imported show has been merged with an existing show.',
    add_booking: 'A booking has been added.',
    attribute_widget_affiliate:
        'A widget booking was attributed to an affiliate via the commission group affiliate code.',
    attribute_integration_commission:
        'An external-integration booking was credited with commission from the agent commission group.',
    delete_booking: 'A booking has been deleted.',
    edit_booking: 'A booking has been edited.',
    send_upgrade_offer: 'An upgrade offer has been sent for this booking.',
    dismiss_upgrade_offer: 'An upgrade offer has been dismissed or declined for this booking.',
    edit_booking_still_billable: 'The still-billable flag has been changed on a cancelled booking.',
    booking_status_change: 'A booking status has been changed.',
    booking_cancel_policy_override:
        'An agent used the always-refund capability to override the cancel policy and issue a refund outside the cancel window.',
    payment: 'A payment has been processed.',
    refund: 'A refund has been processed.',
    add_ticket_option: 'A ticket option has been added.',
    delete_ticket_option: 'A ticket option has been deleted.',
    edit_ticket_option: 'A ticket option has been edited.',
    add_ticket_seat: 'A ticket seat has been added.',
    delete_ticket_seat: 'A ticket seat has been deleted.',
    edit_ticket_seat: 'A ticket seat has been edited.',
    update_ticket_seat: 'A ticket seat has been updated (bulk).',
    add_ticket_type: 'A ticket type has been added.',
    delete_ticket_type: 'A ticket type has been deleted.',
    edit_ticket_type: 'A ticket type has been edited.',
    add_timeslot: 'A timeslot has been added.',
    delete_timeslot: 'A timeslot has been deleted.',
    edit_timeslot: 'A timeslot has been edited.',
    cancel_timeslot: 'A timeslot has been cancelled.',
    uncancel_timeslot: 'A timeslot cancellation has been removed.',
    add_rental_day: 'A rental day (all configured slots) has been created.',
    edit_rental_day: 'A rental day has been moved/edited.',
    send_timeslot_seating_lock_email: 'A timeslot seating-lock email has been sent.',
    set_timeslot_prices: 'Timeslot ticket prices have been updated.',
    send_seating_manifest_copy:
        'A seating manifest copy has been emailed to the show check-in recipients.',
    clear_timeslot_seat_assignments:
        'All seat assignments in a timeslot manifest have been cleared (un-assigned).',
    seating_group_created: 'A booking was linked into a seating group.',
    seating_group_updated: 'A booking was added to or removed from a seating group.',
    seating_group_disbanded: 'A seating group was disbanded.',
    send_booking_seating_changed_email: 'A seating-changed email has been sent to a booking.',
    add_booking_item: 'A booking item has been added.',
    delete_booking_item: 'A booking item has been deleted.',
    edit_booking_item: 'A booking item has been edited.',
    edit_me: 'User has edited his profile.',
    edit_booking_item_checkin_note: 'A booking item checkin note has been edited.',
    add_payment_method: 'A payment method has been added.',
    delete_payment_method: 'A payment method has been deleted.',
    edit_payment_method: 'A payment method has been edited.',
    toggle_payment_method: 'A payment method has been edited (enabled or disabled).',
    manually_trigger_rollback: 'A rollback has been manually triggered.',
    add_more_rollback_time: 'More rollback time has been added to an order.',
    resend_email: 'Email has been sent again.',
    upload_file: 'A file has been uploaded.',
    manual_finish_transaction: 'A transaction has been manually finished.',
    edit_booking_checkin: 'A booking check-in has been edited.',
    add_integration: 'A integration has been added.',
    delete_integration: 'A integration has been deleted.',
    edit_integration: 'A integration has been edited.',
    add_venue_integration: 'A venue integration has been added.',
    edit_venue_integration: 'A venue integration has been edited.',
    viator_tour_list: 'A Viator tour list has been retrieved.',
    viator_availability_check: 'A Viator availability check has been performed.',
    viator_batch_availability: 'A Viator batch availability check has been performed.',
    viator_batch_pricing: 'A Viator batch pricing check has been performed.',
    viator_booking: 'A Viator booking has been performed.',
    viator_booking_create: 'A Viator booking has been created.',
    viator_booking_amendment: 'A Viator booking amendment has been performed.',
    viator_booking_cancellation: 'A Viator booking cancellation has been performed.',
    expedia_get_availability: 'An Expedia availability check has been performed.',
    expedia_create_sale: 'An Expedia booking hold has been created.',
    expedia_commit_sale: 'An Expedia booking has been committed.',
    expedia_release_sale: 'An Expedia booking hold has been released.',
    expedia_create_sale_cancellation: 'An Expedia cancellation hold has been initiated.',
    expedia_commit_sale_cancellation: 'An Expedia cancellation has been committed.',
    expedia_release_sale_cancellation: 'An Expedia cancellation hold has been released.',
    setup_stripe_connect: 'Stripe Connect has been set up for a venue.',
    generate_coupons: 'Coupons have been generated.',
    edit_coupon: 'A coupon has been edited.',
    delete_coupon: 'A coupon has been deleted.',
    limit_coupon: 'A used coupon has been capped at its current usage count.',
    delete_affiliate_invoice: 'An affiliate invoice has been deleted.',
    add_commission_group: 'A commission group has been added.',
    create_commission_group_stripe_customer:
        'A Stripe customer has been created for a commission group.',
    edit_commission_group: 'A commission group has been edited.',
    delete_commission_group: 'A commission group has been deleted.',
    assign_user_commission_group: 'A user has been assigned to a commission group.',
    remove_user_commission_group: 'A user has been removed from a commission group.',
    invite_agent_commission_group: 'An agent has been invited to a commission group.',
    resend_commission_group_welcome: 'A commission group welcome email has been resent to a member.',
    remove_commission_group_member: 'A member has been removed from a commission group.',
    edit_transaction_note: 'A transaction note has been edited.',
    edit_transaction_processing_fee: 'A transaction processing fee has been edited.',
    send_refund_email: 'A refund confirmation email has been sent.',
    send_affiliate_invoice_email: 'An affiliate invoice email has been sent.',
    re_source_booking: 'A booking has been re-sourced to a different agent.',
    change_transaction_payment_type: 'A transaction payment type has been changed.',
    generate_affiliate_invoice: 'An affiliate invoice has been generated.',
    charge_affiliate_invoice: 'An affiliate invoice has been charged.',
    reorder_ticket_options: 'Ticket options have been reordered.',
    reorder_ticket_types: 'Ticket types have been reordered.',
    add_waitlist_subscription: 'A waitlist subscription has been added.',
    delete_waitlist_subscription: 'A waitlist subscription has been deleted.',
    generate_verification_script: 'A show verification script has been generated.',
});

export const CUSTOM_FIELDS_TYPES = Object.freeze({
    SINGLE: 'single',
    MULTI: 'multi',
    DROPDOWN: 'dropdown',
    CHAT: 'chat',
});

export const PAYMENT_METHOD_TYPES = Object.freeze({
    CARD: 'stripe', // 'card: Pay via Card',
    CASH: 'cash', // 'cash: Via Cash',
    NET_RATE: 'net_rate', // 'net_rate: booking is priced at net (retail minus commission); the guest pays the net and the affiliate collects its commission externally'
    NET_RATE_COMPANY: 'net_rate_company', // net_rate_company: agent collects retail externally; platform charges the commission group company card the net (no Stripe Connect)
});

export const BOOKING_STATUSES = Object.freeze({
    PROCESSING: 'processing',
    FINISHED: 'finished',
    CANCELLED: 'cancelled',
});

export const INTEGRATION_DISPLAY_NAMES = Object.freeze({
    fareharbor: 'FareHarbor',
    viator: 'Viator',
});

export const BOOKING_STATUS_COLORS = Object.freeze({
    [BOOKING_STATUSES.PROCESSING]: '#f0ad4e', // 'warning',
    [BOOKING_STATUSES.FINISHED]: '#5cb85c', // 'success',
    [BOOKING_STATUSES.CANCELLED]: '#d9534f', // 'danger',
});

export const ORDER_STATUSES = Object.freeze({
    PENDING: 'pending',
    REFUNDING: 'refunding',
    SETTLED: 'settled',
    ROLLBACK: 'rollback',
    REFUNDED: 'refunded',
});

export const EMAIL_LOG_TYPES = Object.freeze({
    EMAIL: 'email',
    SMS: 'sms',
});

export const INTEGRATION_TYPES = Object.freeze({
    VIATOR: 'viator',
    TRIPADVISOR: 'tripadvisor',
    EXPEDIA: 'expedia',
    GA4: 'ga4',
    FACEBOOK: 'facebook',
    TIKTOK: 'tiktok',
    LINKEDIN: 'linkedin',
    TWITTER: 'twitter',
    SNAPCHAT: 'snapchat',
    PINTEREST: 'pinterest',
    ACTIVITER: 'activiter',
});

// Integrations scoped to the venue rather than a single show (rendered on
// their own /venues/:venueId/integrations page, not the show-scoped one).
export const VENUE_INTEGRATION_TYPES = Object.freeze({
    DYNAMIC_PRICING: 'dynamic_pricing',
});

export const AGE_BAND_TYPES = Object.freeze({
    ADULT: 'adult',
    CHILD: 'child',
    YOUTH: 'youth',
    INFANT: 'infant',
    SENIOR: 'senior',
});
